import "./CMSAddUser.css";

import React, { useEffect, useState } from "react";

import { CMSService } from "../../../services/CMSService";
import { signupUser } from "../../../services/authService";
import { useLoader } from "../../../hooks/useLoader";

interface CMSAddUserProps {
  customerId?: string;
  onClose: () => void;
  onSave: () => void;
}

const CMSAddUser: React.FC<CMSAddUserProps> = ({
  customerId,
  onClose,
  onSave,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { showLoading } = useLoader();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Email validation helper function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation checks
    if (firstName.trim().length < 2) {
      alert("First name must be at least 2 characters long.");
      return;
    }

    if (lastName.trim().length < 2) {
      alert("Last name must be at least 2 characters long.");
      return;
    }

    if (!email.trim()) {
      alert("Please enter an email address.");
      return;
    }

    if (!isValidEmail(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    if (password.length < 6) {
      alert("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    try {
      setIsLoading(true);
      showLoading(true);
      // Combine first name and last name into a single name field
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      await signupUser({
        name: fullName,
        email: email.trim(),
        password: password,
        loginType: "manual",
        referralCode: customerId || "",
      });
      setIsLoading(false);
      showLoading(false);
      alert("User added successfully!");
      onSave();
      onClose();
    } catch (error: any) {
      setIsLoading(false);
      showLoading(false);
      // Email already has an account: offer to attach the existing account
      // to this customer instead (keeps their password and history).
      if (error?.response?.status === 409 && customerId) {
        const attach = window.confirm(
          `${email.trim()} already has an account.\n\nAttach the existing account to this customer instead? Their password stays unchanged and they get this customer's subscription.`
        );
        if (attach) {
          try {
            setIsLoading(true);
            showLoading(true);
            await CMSService.attachUserToCustomer({
              email: email.trim(),
              customerId,
            });
            setIsLoading(false);
            showLoading(false);
            alert("Existing user attached to this customer!");
            onSave();
            onClose();
          } catch (attachError: any) {
            setIsLoading(false);
            showLoading(false);
            alert(
              attachError?.response?.data?.message ||
                "Failed to attach user. Please try again."
            );
          }
        }
        return;
      }
      alert(
        error?.response?.data?.message ||
          "Failed to add user. Please try again."
      );
      console.error("Add user error:", error);
    }
  };

  const isFormValid =
    firstName.trim().length >= 2 &&
    lastName.trim().length >= 2 &&
    email.trim().length > 0 &&
    isValidEmail(email) &&
    password.length >= 6 &&
    password === confirmPassword;

  return (
    <div className="modal-overlay-add">
      <div className="modal-container-add">
        {isLoading && (
          <div className="modal-loader-overlay">
            <img
              src="/assets/loader_white.gif"
              alt="Loading..."
              style={{ width: "60px", height: "60px" }}
            />
          </div>
        )}
        <div className="modal-header-add">
          <div className="modal-title-add">Add User</div>
          <button
            className="close-button"
            onClick={onClose}
            disabled={isLoading}
          >
            X
          </button>
        </div>

        <form onSubmit={handleAddUser}>
          <div className="modal-body-add">
            <div className="input-row-add">
              <span className="input-group-add">
                <label htmlFor="first-name">First Name</label>
                <input
                  type="text"
                  id="first-name"
                  name="firstName"
                  placeholder="Enter first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </span>
              <span className="input-group-add">
                <label htmlFor="last-name">Last Name</label>
                <input
                  type="text"
                  id="last-name"
                  name="lastName"
                  placeholder="Enter last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </span>
            </div>

            <div className="input-row-add">
              <span className="input-group-add">
                <label htmlFor="email">Email ID</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </span>
            </div>

            <div className="input-row-add">
              <span className="input-group-add">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </span>
              <span className="input-group-add">
                <label htmlFor="confirm-password">Confirm Password</label>
                <input
                  type="password"
                  id="confirm-password"
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </span>
            </div>

            <div className="modal-footer-add">
              <button
                type="button"
                className="cancel-button-add"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={
                  isFormValid ? "save-button-add" : "save-button-add-disabled"
                }
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? "Adding..." : "Add User"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CMSAddUser;
